<script setup lang="ts">
import type Product from "~/models/Product";
import ProductImage from "~/models/ProductImage";
import LazyImage from "@/components/utils/LazyImage.vue";
import AddToCartButton from "@/components/buttons/AddToCartButton.vue";
import MainProductCardPromList from "~/components/common/product/MainProductCardPromList.vue";
// import MainSearchProductMonthSelector from "~/components/MainSearchProductMonthSelector.vue";
import SafeHtml from "@/components/utils/SafeHtml.vue";
import {
  breakpointsBootstrapV5,
  useBreakpoints,
  useElementHover,
  useFocus,
  useMounted,
} from "@vueuse/core";
import { computed, ref } from "vue";
import { formatPrice } from "@/utils/pricing";
import ProductHeaderPromIcons from "~/components/common/product/ProductHeaderPromIcons.vue";
import ProductWishlistButton from "@/components/product/ProductWishlistButton.vue";
import auth from "~/services/AuthService";

const props = defineProps<{
  product: Product;
}>();

const cardEl = ref<HTMLElement>();
const isUserLogged = auth.isUserLogged();
const mounted = useMounted();
const showWishlistButton = computed(() => mounted.value && isUserLogged);

const isCardHovered = useElementHover(cardEl);
const { focused: isCardFocused } = useFocus(cardEl);
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileSize = breakpoints.smallerOrEqual("md");
const isMonthDropdownOpened = ref(false);
const isOutOfStock = computed(() => props.product.stock_total === 0);
const shouldUseActiveVersion = computed(
  () => isMobileSize.value || isCardHovered.value || isCardFocused.value,
);
</script>

<template>
  <article
    ref="cardEl"
    class="product-card px-1 px-md-0"
    tabindex="0"
    :data-page="product.page"
  >
    <NuxtLink
      class="card card-lg border-light shadow-sm position-relative product-card-shell"
      :class="{
        active: shouldUseActiveVersion,
        'has-wishlist-button': showWishlistButton,
      }"
      :style="[isMonthDropdownOpened ? 'z-index: 10' : '']"
      :to="props.product.resolveLocation()"
    >
      <div
        v-if="isOutOfStock"
        class="product-card-out-of-stock-overlay d-flex align-items-end justify-content-center w-100 h-100 position-absolute z-1"
      >
        <div
          class="position-absolute h-100 w-100 bg-dark-subtle opacity-25 z-0"
        ></div>
        <span
          class="product-card-out-of-stock-text fs-6 fw-bold text-black-50 z-1"
        >
          AGOTADO TEMPORALMENTE
        </span>
      </div>
      <div
        v-if="showWishlistButton"
        class="position-absolute end-0 top-0 mt-1 me-1 product-card-favorite"
      >
        <product-wishlist-button :product="product" />
      </div>
      <div class="row g-0">
        <!-- Start: Card Image -->
        <div class="col-12">
          <div class="product-cover text-center p-2">
            <lazy-image
              :src="
                product.getCoverImage(ProductImage.Sizes.Small) || undefined
              "
              :alt="product.name || undefined"
              width="120"
              height="120"
            />
          </div>
        </div>
        <!-- / End: Card Image -->
        <!-- Start: Card Body -->
        <div class="col-12">
          <div class="card-body product-card-body p-2 pt-0 px-2 px-md-3">
            <h5
              class="card-title product-card-title text-secondary fs-6 fw-normal mb-0 text-info"
              :class="{
                'text-l2-truncate': !isMobileSize && !shouldUseActiveVersion,
                'text-l3-truncate': isMobileSize,
              }"
            >
              <span>{{ product.name }}</span>
            </h5>
            <div
              v-if="product.brand"
              class="product-brand product-card-brand d-inline-block text-center pe-2 mb-2"
            >
              <span class="fw-bold small">
                {{ product.brand.name }}
              </span>
            </div>
            <product-header-prom-icons
              class="mb-3 product-card-prom-icons"
              :product="product"
            />
            <p
              class="card-text product-card-price-row mb-0 d-flex align-items-center gap-2 lh-normal"
            >
              <span
                class="text-secondary small-sm fw-light product-card-price-label"
                >{{ product.hasDiscount() ? "Normal" : "Precio" }}</span
              >
              <safe-html
                tag="span"
                class="product-card-base-price"
                :html="
                  formatPrice(
                    product.getFinalCommonPrice(),
                    true,
                    product.hasDiscount()
                      ? 'text-secondary small text-black-50 text-decoration-line-through'
                      : 'fs-6 fw-semibold',
                  )
                "
              />
            </p>
            <p
              v-if="product.hasDiscount()"
              class="card-text product-card-price-row product-card-discount-row mt-md-1 mb-0 d-flex align-items-center gap-2 lh-normal"
            >
              <span
                class="text-secondary small-sm fw-light text-truncate product-card-price-label"
              >
                Promoción contado
              </span>
              <safe-html
                class="text-secondary fs-4 fw-bold text-danger product-card-current-price"
                tag="span"
                :html="formatPrice(product.getFinalCurrentPrice())"
              />
              <span
                v-if="product.mwi_one_disc_percent > 0.1"
                class="text-danger fs-6 product-card-discount-note"
              >
                ¡Descuento a un pago!
              </span>
            </p>
            <!-- <main-product-card-month-selector
              class="mt-2 mt-md-3"
              :product="product"
              @is-opened="isMonthDropdownOpened = $event"
            /> -->
          </div>
        </div>
        <!-- / End: Card Body -->
      </div>
      <!-- Start: Card Footer -->
      <div class="mt-auto">
        <div
          class="card-footer product-card-footer d-flex justify-content-between bg-transparent border-0 p-2 pt-1 px-2 px-md-3 flex-wrap gap-1 gap-md-2"
        >
          <main-product-card-prom-list :product="product" />
          <add-to-cart-button
            v-slot="slotProps"
            :product="product"
            :btn-add-from-search="true"
            :to="$route.fullPath"
          >
            <button
              class="btn btn-outline-secondary rounded-0 text-nowrap align-self-stretch ms-auto product-card-cart-btn"
              :class="[isMobileSize ? 'btn-lg' : 'btn-lg']"
              :style="{
                '--bs-btn-bg': '#2B2D421C',
                '--bs-btn-color': '#4B4B4B',
                '--bs-btn-border-color': '#2B2D421C',
              }"
              @click.prevent="slotProps.click(1)"
            >
              &nbsp;<i class="bi bi-cart"></i>&nbsp;
            </button>
          </add-to-cart-button>
        </div>
      </div>
      <!-- / End: Card Footer -->
    </NuxtLink>
  </article>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/bootstrap-helpers" as *;

$card-desktop-height: 480px;

.product-card {
  position: relative;
  z-index: 0;
  height: auto;
  @include media-breakpoint-up(md) {
    height: $card-desktop-height;
  }
  cursor: pointer;

  &:hover,
  &:focus-within {
    z-index: 6;
  }

  .card {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    z-index: 1;
    min-height: 100%;
    max-height: $card-desktop-height;
    overflow: hidden;
    border-color: rgb(229 232 236) !important;
    border-radius: 0.55rem;
    background: linear-gradient(180deg, rgb(255 255 255), rgb(251 252 253));
    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
    &.active {
      box-shadow: var(--bs-box-shadow) !important;
      z-index: 4;
      max-height: $card-desktop-height + 100px;
      transition: 0.3s max-height ease-in-out;
      animation-duration: 0.3s;
      animation-name: visible-card-after-height;
    }
  }

  .row {
    flex: 1 1 auto;
    align-content: start;
  }
}
// Ayuda a que se pueda aplicar visible para elementos como dropdowns justo al terminar la transición de height.
@keyframes visible-card-after-height {
  from {
    overflow: hidden;
  }
  to {
    overflow: visible;
  }
}
.product-brand {
  text-transform: uppercase;
}

.product-card-shell:hover,
.product-card-shell:focus-visible {
  border-color: rgb(220 53 69 / 0.18) !important;
  box-shadow: 0 10px 24px rgb(21 21 21 / 0.06) !important;
}

.product-card-favorite {
  z-index: 2;
}

.product-card-favorite :deep(.btn) {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid rgb(225 228 232);
  border-radius: 0.45rem;
  background: rgb(255 255 255 / 0.92);
  box-shadow: 0 4px 12px rgb(21 21 21 / 0.05);

  @include media-breakpoint-up(md) {
    width: 38px;
    height: 38px;
  }
}

.product-card-favorite :deep(.btn:hover),
.product-card-favorite :deep(.btn:focus) {
  border-color: rgb(220 53 69 / 0.2);
  background: rgb(255 250 250);
  color: rgb(220 53 69) !important;
  opacity: 1 !important;
}

.product-card-favorite :deep(.btn:hover i),
.product-card-favorite :deep(.btn:focus i),
.product-card-favorite :deep(.btn-outline-secondary:hover i),
.product-card-favorite :deep(.btn-outline-secondary:focus i),
.product-card-favorite :deep(.btn-light:hover i),
.product-card-favorite :deep(.btn-light:focus i) {
  color: rgb(220 53 69) !important;
}

.product-card-favorite :deep(.btn-outline-secondary:hover),
.product-card-favorite :deep(.btn-outline-secondary:focus),
.product-card-favorite :deep(.btn-light:hover),
.product-card-favorite :deep(.btn-light:focus) {
  background: rgb(255 250 250) !important;
  border-color: rgb(220 53 69 / 0.2) !important;
  box-shadow: 0 4px 12px rgb(220 53 69 / 0.08);
}

.product-card-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 0.22rem;
  width: 100%;
  padding-bottom: 0.6rem !important;
}

.product-card-shell.has-wishlist-button .product-card-body {
  padding-right: 2.8rem !important;
}

.product-card-title {
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  margin-right: 0;
  color: rgb(31 35 42) !important;
  line-height: 1.4;
  letter-spacing: -0.01em;
  text-align: left;
}

.product-card-brand {
  min-height: auto;
  margin-top: 0rem;
  margin-bottom: 0.45rem !important;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: rgb(63 69 78);
}

.product-card-brand span {
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.product-card-prom-icons {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: 0.8rem !important;
}

.product-card-prom-icons :deep(.badge) {
  min-height: 28px;
  padding: 0.36rem 0.58rem;
  border: 1px solid rgb(232 235 239);
  border-radius: 999px;
  background: rgb(248 249 250) !important;
  color: rgb(82 90 101) !important;
}

.product-card-prom-icons :deep(.badge .small) {
  font-size: 0.65rem;
  letter-spacing: 0.02em;
}

.product-card-prom-icons :deep(svg),
.product-card-prom-icons :deep(i) {
  flex-shrink: 0;
}

.product-card-price-row {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  flex-wrap: wrap;
  gap: 0.45rem !important;
  color: rgb(89 96 106);
}

.product-card-price-label {
  color: rgb(109 115 124) !important;
  font-size: 0.72rem;
}

.product-card-base-price :deep(.text-black-50) {
  color: rgb(155 160 168) !important;
}

.product-card-base-price :deep(.fs-6),
.product-card-base-price :deep(.small),
.product-card-current-price :deep(*) {
  line-height: 1.2;
}

.product-card-discount-row {
  align-items: baseline !important;
  flex-wrap: wrap;
  row-gap: 0.2rem;
}

.product-card-current-price {
  color: rgb(203 42 61) !important;
  display: inline-flex;
  align-items: baseline;
}

.product-card-discount-note {
  font-size: 0.72rem !important;
  font-weight: 600;
  color: rgb(212 61 78) !important;
}

.product-card-out-of-stock-overlay {
  pointer-events: none;
}

.product-card-out-of-stock-text {
  margin-bottom: 3.5rem;
}

.product-card-footer {
  align-items: center;
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 0.8rem !important;
  border-top: 1px solid rgb(239 241 244) !important;
}

.product-card-shell > .mt-auto {
  margin-top: auto;
  flex-shrink: 0;
}

.product-card-footer :deep(.product-card-promotion-list) {
  gap: 0.35rem !important;
  flex: 1 1 auto;
  min-width: 0;
}

.product-card-footer :deep(.product-card-promotion-list li) {
  min-height: 34px;
  padding: 0.3rem 0.45rem !important;
  border: 1px solid rgb(234 236 240);
  border-radius: 0.45rem;
  background: rgb(249 250 251);
}

.product-card-footer :deep(.product-card-promotion-list li p) {
  color: rgb(96 103 113);
}

.product-card-footer :deep(.product-card-promotion-list li .highlight-text) {
  color: rgb(42 47 55);
}

.product-card-cart-btn {
  min-width: 44px;
  min-height: 40px;
  border: 1px solid rgb(227 230 234) !important;
  border-radius: 0.45rem !important;
  background: linear-gradient(
    180deg,
    rgb(248 249 250),
    rgb(243 244 246)
  ) !important;
  color: rgb(81 87 97) !important;
  transition:
    border-color 0.18s ease,
    background-color 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease;

  @include media-breakpoint-up(md) {
    min-width: 50px;
  }
}

.product-card-cart-btn:hover,
.product-card-cart-btn:focus {
  border-color: rgb(220 53 69 / 0.22) !important;
  background: rgb(255 248 248) !important;
  color: rgb(220 53 69) !important;
  box-shadow: 0 6px 16px rgb(220 53 69 / 0.08);
}

.product-cover {
  padding-top: 0.8rem !important;
  padding-bottom: 0.35rem !important;
  min-height: 170px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 120px;
  }
  @include media-breakpoint-up(md) {
    min-height: 182px;

    img {
      max-height: 160px;
    }
  }
}
</style>
